/* parseVendorForecastExcel.js -- THE vendor-forecast template's parser (2026-10-29, Brian -- Step 6's "fill vendor
 * forecasts from a file"). It reads the rows only; matching each row to a staged vendor and building the change list is
 * utils/vendorForecastFile.js, and the upload check is utils/specCheck.js (the specScan below is what it reads).
 *
 * Headers: the spec's own aliases (templateSpecs.js vendor_forecast), matched through the ONE header rule
 * (templateHeaders.normalizeHeader). The tab: the first whose name contains "forecast", else the first tab.
 * Numbers are read the upload check's way ("$", ",", "%" and spaces ignored, "(1,000)" = -1000) and rounded to cents at
 * the parse (CLAUDE.md section 5, ingestion rounding); a cell that is not a number reads as null and is the upload
 * check's B6 to name. Months: parseVendorExcel.parseStartMonth (text "YYYY-MM", a real Excel date, "MM/DD/YYYY",
 * "Jan-23").
 *
 * Plain ASCII only. */
import * as XLSX from 'xlsx';
import { normalizeHeader, aliasSet } from './templateHeaders';
import { specColumnAliases, TEMPLATE_SPECS } from './templateSpecs';
import { parseStartMonth } from './parseVendorExcel';
import { normalizeVendorType, normalizeSeatBasis } from './vendorForecastTypes';
import { FREQ_OPTIONS } from './compCycle';

const r2 = n => Math.round(n * 100) / 100;
const COLS = TEMPLATE_SPECS.vendor_forecast.columns.map(c => c.name);

/* THE HEADER ALIASES THIS PARSER READS -- the spec's own arrays, by reference (the spec gate asserts identity). */
export const VENDOR_FORECAST_HEADER_COLUMNS = Object.fromEntries(COLS.map(c => [`vendor_forecast.${c}`, specColumnAliases('vendor_forecast', c)]));
const HS = Object.fromEntries(COLS.map(c => [c, aliasSet(VENDOR_FORECAST_HEADER_COLUMNS[`vendor_forecast.${c}`])]));

const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');
const text = (v) => (isBlank(v) ? '' : String(v).trim());

/* A number cell -> a number (cents), 0 for a blank / "-", or null when it is not a number (B6 names it). */
export function readVfNumber(v, round = true) {
  const rr = round ? r2 : (n => n);
  if (isBlank(v)) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? rr(v) : null;
  const s = String(v).trim();
  const stripped = s.replace(/[$,()%\s]/g, '');
  if (stripped === '-' || stripped === '') return 0;
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(stripped)) return null;
  const n = parseFloat(stripped);
  return rr(/\(.*\)/.test(s) ? -Math.abs(n) : n);
}
/* A frequency cell -> its stored value (FREQ_OPTIONS, the one owner), or null. */
export function normalizeVfFrequency(v) {
  const k = text(v).toLowerCase();
  if (!k) return null;
  const hit = FREQ_OPTIONS.find(o => o.value === k || o.label.toLowerCase() === k);
  if (hit) return hit.value;
  return k === 'semi annual' ? 'semi_annual' : null;
}
/* An end-month cell: blank / "Until Canceled" -> 'until_canceled'; a month -> YYYY-MM; else null. */
export function readVfEnd(v) {
  if (isBlank(v)) return 'until_canceled';
  if (/^until[\s_-]*cancel+ed$/i.test(text(v))) return 'until_canceled';
  return parseStartMonth(v);
}

function pickSheet(wb) {
  return wb.SheetNames.find(n => /forecast/i.test(n)) || wb.SheetNames[0];
}

export function parseVendorForecastExcel(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
  const sheetName = pickSheet(wb);
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, raw: true, defval: '' });
  // the header row: the first of the first 10 rows that names both vendor_name and forecast_type
  let headerRow = -1;
  for (let i = 0; i < Math.min(10, rows.length) && headerRow < 0; i++) {
    const h = (rows[i] || []).map(normalizeHeader);
    if (h.some(x => HS.vendor_name.has(x)) && h.some(x => HS.forecast_type.has(x))) headerRow = i;
  }
  const hdr = (rows[headerRow < 0 ? 0 : headerRow] || []).map(normalizeHeader);
  const cols = {};
  for (const c of COLS) { const i = hdr.findIndex(x => HS[c].has(x)); cols[c] = i >= 0 ? i : null; }
  const specScan = { template: 'vendor_forecast', layout: 'template', headerRow: headerRow < 0 ? 0 : headerRow, cols, monthCols: {}, extraNumberCols: [], rows };
  const out = [];
  if (headerRow >= 0) {
    for (let i = headerRow + 1; i < rows.length; i++) {
      const r = rows[i] || [];
      const get = (c) => (cols[c] == null ? '' : r[cols[c]]);
      if (COLS.every(c => isBlank(get(c)))) continue;
      const depsRaw = text(get('seat_departments'));
      out.push({
        row: i + 1,
        is_category: text(get('is_category')),
        gl_line_name: text(get('gl_line_name')),
        vendor_name: text(get('vendor_name')),
        forecast_type: normalizeVendorType(get('forecast_type')),
        forecast_type_raw: text(get('forecast_type')),
        amount: readVfNumber(get('amount')),
        frequency: normalizeVfFrequency(get('frequency')),
        billing_month: isBlank(get('billing_month')) ? null : parseStartMonth(get('billing_month')),
        per_seat_cost: readVfNumber(get('per_seat_cost')),
        seat_basis: normalizeSeatBasis(get('seat_basis')),
        seat_departments: depsRaw ? depsRaw.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [],
        seat_departments_raw: depsRaw,
        // the percent as written, not rounded: a third decimal is refused by the one percent rule (utils/percentText.js)
        pct_rate: readVfNumber(get('pct_rate'), false),
        pct_base: text(get('pct_base')) || null,
        start_month: isBlank(get('start_month')) ? null : parseStartMonth(get('start_month')),
        end_month: readVfEnd(get('end_month')),
      });
    }
  }
  return { sheetName, rows: out, flags: [], specScan, headerFound: headerRow >= 0 };
}

export default parseVendorForecastExcel;
